import { FeedbackLightBulbIcon, PlusIcon } from "@xola/icons";
import { Button, Tooltip } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React, { useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { VariantGroup } from "types";
import { DeleteVariantGroupModal } from "./DeleteVariantGroupModal";
import { EditVariantGroupModal } from "./EditVariantGroupModal";
import { VariantGroupsDndTable } from "./VariantGroupsDndTable";
import { useModal } from "@/hooks/useVisibility";
import { reorder } from "@/utils/reorder";

export const MAX_VARIANT_GROUPS = 3;

interface VariantGroupsTableProps {
    variantGroups: VariantGroup[];
    onReorder: (groups: VariantGroup[]) => void;
    onDelete: (groupIndex: number) => void;
    onSave: (group: VariantGroup, editingIndex?: number) => void;
}

export const VariantGroupsTable = ({ variantGroups, onReorder, onDelete, onSave }: VariantGroupsTableProps) => {
    const canAddMore = variantGroups.length < MAX_VARIANT_GROUPS;
    const addGroupModal = useModal();
    const deleteGroupModal = useModal();
    const [editingGroup, setEditingGroup] = useState<VariantGroup>();
    const [editingGroupIndex, setEditingGroupIndex] = useState<number>();
    const [deletingGroup, setDeletingGroup] = useState<VariantGroup>();
    const [deletingGroupIndex, setDeletingGroupIndex] = useState<number>();

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        const reorderedGroups = reorder(variantGroups, result.source.index, result.destination.index);
        onReorder(reorderedGroups);
    };

    const getDisplayName = (group: VariantGroup, index: number) => {
        const parentNames = variantGroups.slice(0, index).map((g) => g.name);
        return parentNames.length > 0 ? `${parentNames.join(" > ")} > ${group.name}` : group.name;
    };

    const handleOpenAddModal = () => {
        setEditingGroup(undefined);
        setEditingGroupIndex(undefined);
        addGroupModal.show();
    };

    const handleEditGroup = (group: VariantGroup, index: number) => {
        setEditingGroup(group);
        setEditingGroupIndex(index);
        addGroupModal.show();
    };

    const handleSaveGroup = (group: VariantGroup) => {
        onSave(group, editingGroupIndex);
        setEditingGroup(undefined);
        setEditingGroupIndex(undefined);
    };

    const handleOpenDeleteModal = (group: VariantGroup, index: number) => {
        setDeletingGroup(group);
        setDeletingGroupIndex(index);
        deleteGroupModal.show();
    };

    const handleConfirmDelete = () => {
        if (deletingGroupIndex !== undefined) {
            onDelete(deletingGroupIndex);
            setDeletingGroup(undefined);
            setDeletingGroupIndex(undefined);
            deleteGroupModal.hide();
        }
    };

    const handleDeleteGroup = (group: VariantGroup, index: number) => {
        handleOpenDeleteModal(group, index);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h6 className="font-bold">Variant Groups</h6>
                <Tooltip content="A product may have up to 3 variant groups.">
                    <SecondaryButton size="small" disabled={!canAddMore} onClick={handleOpenAddModal}>
                        <PlusIcon size="small" className="mr-1" />
                        Add Variant Group
                    </SecondaryButton>
                </Tooltip>
            </div>

            {variantGroups.length > 0 && (
                <VariantGroupsDndTable
                    variantGroups={variantGroups}
                    getDisplayName={getDisplayName}
                    onDragEnd={handleDragEnd}
                    onEditGroup={handleEditGroup}
                    onDeleteGroup={handleDeleteGroup}
                />
            )}

            {variantGroups.length === 0 && (
                <div className="flex items-start gap-3 rounded-lg bg-primary-lighter p-4">
                    <FeedbackLightBulbIcon size="medium" className="mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <h6 className="font-bold">Variant Groups</h6>
                        <p className="text-sm">
                            You don&apos;t have any variant groups for this product yet. Add a new variant group now
                            (e.g. size, color, etc).
                        </p>
                        <div className="mt-3 flex justify-end gap-2">
                            {/* TODO XS-1418: Add import CSV functionality */}
                            {/* <SecondaryButton size="small" type="button">
                                Import CSV
                            </SecondaryButton> */}
                            <Button size="small" type="button" onClick={handleOpenAddModal}>
                                <PlusIcon size="small" className="mr-1" />
                                Create First Variant Group
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <EditVariantGroupModal
                isOpen={addGroupModal.isVisible}
                group={editingGroup}
                variantGroups={variantGroups}
                onClose={addGroupModal.hide}
                onSave={handleSaveGroup}
            />

            <DeleteVariantGroupModal
                isOpen={deleteGroupModal.isVisible}
                group={deletingGroup}
                onClose={deleteGroupModal.hide}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};
